// The Schedules page's pure rules (docs/tasks/build-spec.md §9.3, §9.4,
// §10.6; scheduleOverviewModel.selftest.ts runs them):
//
//   groups     task schedules · workflow timetables · routines, the
//              system routines (check-ins, inbox watchers, heartbeats …)
//              apart under a fold
//   words      when it runs ("Weekdays at 07:00", the routine's own
//              display read as words) and how its last run went
//   the form   a task schedule: a name, the give form (who does it, what
//              to do, the kind, the time limit, the browser) and a cron
//              in a zone; what the server would refuse, and the body
//
// Only task schedules are made here; routines and workflow timetables are
// edited on their own pages (the row links there).

import type { ScheduleRowOut } from "@/api/scheduleOverview";
import type { TaskScheduleBody, TaskSchedulePatch, TaskScheduleOut } from "@/api/taskSchedules";
import type { TaskKind } from "@/api/tasks";
import type { StatusTone } from "@/components/ui/StatusBadge";
import { EMPTY_GIVE, TASK_DEFAULT_MINUTES, giveBody, giveProblems, isTaskStatus, taskStatusWord, type GiveField, type GiveForm, type GiveWorkerKind } from "@/features/tasks/taskModel";
import { DEFAULT_SCHEDULE, DEFAULT_TZ } from "@/features/workflows/editor/scheduleModel";
import { statusWord } from "@/features/workflows/status";
import { firesBetween, formatSchedule, humanizeCron, nextFires, parseCron, parseInterval } from "@/lib/cron";
import { toDate } from "@/lib/format";

// ─── the list ───

// "schedule": a row of the one schedules store (a workspace switched to it,
// docs/tasks/schedules-unified.md); its page replaces this one in the next build.
export type GroupKey = "schedule" | "task" | "workflow" | "routine" | "system";

export const GROUP_LABEL: Readonly<Record<GroupKey, string>> = {
  schedule: "Schedules",
  task: "Task schedules",
  workflow: "Workflow timetables",
  routine: "Routines",
  system: "System routines",
};

/** The rows by group, in the server's order (by name within each). */
export function groupRows(rows: readonly ScheduleRowOut[]): Record<GroupKey, ScheduleRowOut[]> {
  const out: Record<GroupKey, ScheduleRowOut[]> = { schedule: [], task: [], workflow: [], routine: [], system: [] };
  for (const r of rows) out[r.source === "routine" && r.system ? "system" : r.source].push(r);
  return out;
}

/** "task:ts_0a1b2c3d" → "ts_0a1b2c3d"; null for any other row. */
export function taskScheduleId(row: Pick<ScheduleRowOut, "id" | "source">): string | null {
  return row.source === "task" && row.id.startsWith("task:") ? row.id.slice(5) : null;
}

/** What a list says for a timetable it cannot put in words: never the raw expression (the editor keeps it). */
export const CUSTOM_TIMES = "Custom times";

/** A cron in a list: "Quarterly on the 20th at 10:00", else "Custom times". */
export function cronListWords(cron: string): string {
  return humanizeCron(cron) ?? CUSTOM_TIMES;
}

/** A routine's own display in a list ("every 20m" → "Every 20 min"); a cron it cannot read is "Custom times". */
export function displayListWords(display: string | null | undefined): string {
  const f = formatSchedule({ schedule_display: display ?? null });
  if (!f.text) return "";
  return f.text === f.raw && parseCron(f.raw) ? CUSTOM_TIMES : f.text;
}

/** When it runs, in words: the cron read out, else the routine's own display ("Custom times" rather than a raw cron). */
export function whenWords(row: Pick<ScheduleRowOut, "cron" | "schedule" | "source">): string {
  if (row.source !== "routine" && row.cron) return cronListWords(row.cron);
  return displayListWords(row.schedule || row.cron) || "—";
}

export interface RowWord {
  label: string;
  tone: StatusTone;
}

// ─── a slot that did not start, in plain words (both Schedules pages) ───

/** What a row knows about its slots: the engine's text, its runs and its timetable. */
export interface SlotFacts {
  last_error?: string | null;
  last_run_at?: string | null;
  next_run_at?: string | null;
  cron?: string | null;
  every_minutes?: number | null;
  /** A one-off's instant. */
  at?: string | null;
}

export interface LastProblem {
  /** True when the slot that did not start is the latest one (nothing ran since). */
  latest: boolean;
  /** "Did not start" (danger) when it is the latest; "Missed one" (warning) when a later slot ran. */
  badge: RowWord;
  /** One plain line: "The agents weren't reachable." / "The agents weren't reachable. The next one ran." */
  line: string;
  /** The engine's own text, for Details. */
  detail: string;
}

const DAY = 86_400_000;

/**
 * The slot before `next_run_at`: an interval's step back, a cron's previous
 * fire (read in this browser's zone and moved by however far its fire next
 * to `next_run_at` is from it, so a schedule kept in another zone lines up),
 * a one-off's own time. null when that cannot be told.
 */
export function previousSlot(f: SlotFacts): Date | null {
  if (f.at && !f.cron && !f.every_minutes) return toDate(f.at);
  const next = Date.parse(f.next_run_at ?? "");
  if (!Number.isFinite(next)) return null;
  if (f.every_minutes && f.every_minutes > 0) return new Date(next - f.every_minutes * 60_000);
  if (!f.cron || !parseCron(f.cron)) return null;
  const fires = firesBetween(f.cron, new Date(next - 9 * DAY), new Date(next + DAY));
  let i = -1;
  for (let k = 0; k < fires.length; k += 1) if (i < 0 || Math.abs(fires[k].getTime() - next) < Math.abs(fires[i].getTime() - next)) i = k;
  if (i <= 0) return null;
  return new Date(fires[i - 1].getTime() + (next - fires[i].getTime()));
}

/** The engine's reason in plain words, by what it says; "it couldn't start" for anything else. */
const REASONS: readonly [RegExp, string][] = [
  [/switched off|not enabled/i, "tasks were switched off"],
  [/operator's OK|approv/i, "it was waiting for your OK"],
  [/still (?:going|running)/i, "the previous run was still going"],
  [/may no longer give/i, "its agent may no longer give this work"],
  [/tasks today|per day|daily limit/i, "its agent hit today's limit"],
  [/given minutes before|already queued/i, "the same work had just been given"],
  [/lattice (?:was not running|restarted)|never recorded/i, "Corvus was restarting"],
  [/gateway|did not answer|unreachable|not reachable|connection|timed? ?out/i, "the agents weren't reachable"],
];

export function problemReason(text: string): string {
  return REASONS.find(([re]) => re.test(text))?.[1] ?? "it couldn't start";
}

const capital = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * A slot that did not start, said plainly (null: none). The engine keeps
 * its last refusal; it is the latest slot's unless a run came after that
 * slot. Only then is it loud (danger, "Did not start"); one a later slot
 * made up for is a warning, "Missed one".
 */
export function lastProblem(f: SlotFacts): LastProblem | null {
  const detail = (f.last_error ?? "").trim();
  if (!detail) return null;
  const reason = problemReason(detail);
  const slot = previousSlot(f);
  const ran = Date.parse(f.last_run_at ?? "");
  // A run at the latest slot (or after it) means the refusal is an older one's.
  const latest = !(slot && Number.isFinite(ran) && ran >= slot.getTime() - 60_000);
  if (!latest) return { latest, badge: { label: "Missed one", tone: "warning" }, line: `${capital(reason)}. The next one ran.`, detail };
  // No time of its own: the engine's text (behind Details) names the slot, and a second guess at it could disagree.
  return { latest, badge: { label: "Did not start", tone: "danger" }, line: `${capital(reason)}.`, detail };
}

/** A row of this page as slot facts (a routine's timetable is its own display: a cron or an interval). */
export function rowSlotFacts(row: Pick<ScheduleRowOut, "source" | "cron" | "schedule" | "last_error" | "last_run_at" | "next_run_at">): SlotFacts {
  const display = (row.schedule ?? "").trim();
  const interval = parseInterval(display);
  const cron = row.cron || (parseCron(display) ? display : null);
  return { last_error: row.last_error, last_run_at: row.last_run_at, next_run_at: row.next_run_at, cron, every_minutes: interval ? interval.seconds / 60 : null };
}

const ROUTINE_OK: ReadonlySet<string> = new Set(["ok", "success", "succeeded"]);

/** How the last run went (null: none yet); a slot that could not start says so first (lastProblem). */
export function lastWord(row: Pick<ScheduleRowOut, "source" | "last_status" | "last_error"> & Partial<Pick<ScheduleRowOut, "cron" | "schedule" | "last_run_at" | "next_run_at">>): RowWord | null {
  if (row.last_error) return lastProblem(rowSlotFacts({ cron: null, schedule: "", last_run_at: null, next_run_at: null, ...row }))?.badge ?? null;
  const s = (row.last_status ?? "").trim();
  if (!s) return null;
  if (row.source === "task") {
    const w = isTaskStatus(s) ? taskStatusWord(s) : null;
    return w ? { label: w.label, tone: w.tone } : { label: s, tone: "neutral" };
  }
  if (row.source === "workflow") {
    const w = statusWord(s);
    return { label: w.label, tone: w.tone };
  }
  const low = s.toLowerCase();
  if (ROUTINE_OK.has(low)) return { label: "OK", tone: "success" };
  if (low === "skipped") return { label: "Skipped", tone: "neutral" };
  return { label: s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " "), tone: "danger" };
}

// ─── the task schedule form ───

export const MAX_NAME = 80;
export const MIN_GAP_MINUTES = 15;

export interface ScheduleForm extends GiveForm {
  name: string;
  cron: string;
  tz: string;
  enabled: boolean;
}

export const EMPTY_SCHEDULE_FORM: ScheduleForm = { ...EMPTY_GIVE, name: "", cron: DEFAULT_SCHEDULE.cron, tz: DEFAULT_TZ, enabled: true };

/** A saved schedule read into the form (a schedule without a limit shows the default). */
export function formFromSchedule(s: TaskScheduleOut): ScheduleForm {
  return {
    name: s.name,
    to: s.to,
    title: s.title,
    brief: s.brief,
    kind: (s.kind === "question" ? "question" : "task") as TaskKind,
    minutes: s.minutes ?? TASK_DEFAULT_MINUTES,
    needsBrowser: s.needs_browser,
    cron: s.cron,
    tz: s.tz || DEFAULT_TZ,
    enabled: s.enabled,
  };
}

/** What the server refuses in a cron: one it cannot read, fires under `floor` (15) minutes apart. */
export function cronProblem(cron: string, floor: number = MIN_GAP_MINUTES): string | null {
  if (!parseCron(cron)) return "Five fields: minute hour day month weekday (e.g. 30 6 * * 1-5).";
  const fires = nextFires(cron, new Date(2026, 0, 5), 60);
  for (let i = 1; i < fires.length; i += 1) {
    if (fires[i].getTime() - fires[i - 1].getTime() < floor * 60_000) return `At least ${floor} minutes apart.`;
  }
  return null;
}

export type ScheduleField = GiveField | "name" | "cron";

/** What stops the form from being saved, by field ({} = it can go). */
export function scheduleProblems(form: ScheduleForm, worker: GiveWorkerKind): Partial<Record<ScheduleField, string>> {
  const out: Partial<Record<ScheduleField, string>> = giveProblems(form, worker);
  if (!form.name.trim()) out.name = "Give it a name.";
  else if (form.name.trim().length > MAX_NAME) out.name = `At most ${MAX_NAME} characters.`;
  const cron = cronProblem(form.cron);
  if (cron) out.cron = cron;
  return out;
}

/** The create body: the give body plus the name and the timetable. */
export function scheduleBody(form: ScheduleForm, worker: GiveWorkerKind): TaskScheduleBody {
  return { ...giveBody(form, worker), name: form.name.trim(), cron: form.cron.trim(), tz: form.tz, enabled: form.enabled };
}

/** An edit sends every field; no limit (a question, a workflow) clears the saved one. */
export function schedulePatch(form: ScheduleForm, worker: GiveWorkerKind): TaskSchedulePatch {
  const body = scheduleBody(form, worker);
  return { ...body, minutes: body.minutes ?? null };
}
